import {
  AddAutomationResourceArgs,
  AddAutomationResourceResponse,
  AddAutomationResourcesPayload,
  AddAutomationResourcesResponse,
  AutomationAnalyticsResponse,
  AutomationDetailAnalyticsResponse,
  AutomationResourceTypesResponse,
  AutomationStatusResponse,
  AutomationTemplatePayload,
  AutomationTemplateDetail,
  AutomationTemplateResponse,
  AutomationTemplatesResponse,
  AutomationTriggerResponse,
  CreateAutomationPayload,
  CreateAutomationResponse,
  CreateAutomationTemplatePayload,
  DeleteAutomationResponse,
  GetAutomationResourcesParams,
  GetAutomationResourcesResponse,
  GetAutomationsParams,
  GetAutomationsResponse,
  GetSystemAutomationsResponse,
  RemoveAutomationResourceResponse,
  UpdateAutomationPayload,
} from "interfaces";
import instance from "lib/axiosInterceptor";

const AUTOMATIONS_URL = "/Automations";
const SYSTEM_AUTOMATIONS_URL = "/System/Automations";

const jsonBodyConfig = {
  headers: { "Content-Type": "application/json" },
};

export async function createAutomationService(
  automation: CreateAutomationPayload,
): Promise<CreateAutomationResponse> {
  const response = await instance.post<CreateAutomationResponse>(
    AUTOMATIONS_URL,
    automation,
    jsonBodyConfig,
  );
  return response.data;
}

export async function getAutomationsService(
  params: GetAutomationsParams = {},
): Promise<GetAutomationsResponse> {
  const response = await instance.get<GetAutomationsResponse>(AUTOMATIONS_URL, {
    params,
  });
  return response.data;
}

export async function getSystemAutomationsService(): Promise<GetSystemAutomationsResponse> {
  const response = await instance.get<GetSystemAutomationsResponse>(
    SYSTEM_AUTOMATIONS_URL,
  );
  return response.data;
}

export async function updateAutomationService({
  id,
  name,
  description,
  eventId,
  payload,
}: UpdateAutomationPayload): Promise<any> {
  const response = await instance.put(
    `${AUTOMATIONS_URL}/${id}`,
    { name, description, eventId, payload },
    jsonBodyConfig,
  );
  return response.data;
}

export async function deleteAutomationService(
  id: string,
): Promise<DeleteAutomationResponse> {
  const response = await instance.delete<DeleteAutomationResponse>(
    `${AUTOMATIONS_URL}/${id}`,
  );
  return response.data;
}

export async function publishAutomationService(
  id: string,
): Promise<AutomationStatusResponse> {
  const response = await instance.post<AutomationStatusResponse>(
    `${AUTOMATIONS_URL}/${id}/Publish`,
  );
  return response.data;
}

export async function draftAutomationService(
  id: string,
): Promise<AutomationStatusResponse> {
  const response = await instance.post<AutomationStatusResponse>(
    `${AUTOMATIONS_URL}/${id}/Draft`,
  );
  return response.data;
}

export async function getAutomationAnalyticsService(): Promise<AutomationAnalyticsResponse> {
  const response = await instance.get<AutomationAnalyticsResponse>(
    `${AUTOMATIONS_URL}/Analytics`,
  );
  return response.data;
}

export async function getAutomationDetailAnalyticsService(
  id: string,
  recentRunsLimit = 10,
): Promise<AutomationDetailAnalyticsResponse> {
  const response = await instance.get<AutomationDetailAnalyticsResponse>(
    `${AUTOMATIONS_URL}/${id}/Analytics`,
    { params: { recentRunsLimit } },
  );
  return response.data;
}

export async function getAutomationTriggersService(): Promise<AutomationTriggerResponse> {
  const response = await instance.get<AutomationTriggerResponse>(
    `${AUTOMATIONS_URL}/Triggers`,
  );
  return response.data;
}

export async function getAutomationActionTypesService(): Promise<unknown> {
  const response = await instance.get(`${AUTOMATIONS_URL}/Actions`);
  return response.data;
}

/** Runtime configuration for condition, wait, and supported-node controls. */
export async function getWorkflowOptionsService(): Promise<unknown> {
  const response = await instance.get(`${AUTOMATIONS_URL}/WorkflowOptions`);
  return response.data;
}

export async function createAutomationTemplateService(
  body: CreateAutomationTemplatePayload,
): Promise<AutomationTemplateResponse> {
  // The endpoint now binds a `{ name, description, payload }` object, where
  // `payload` is the workflow JSON as a string.
  const response = await instance.post<AutomationTemplateResponse>(
    `${AUTOMATIONS_URL}/Templates`,
    body,
    jsonBodyConfig,
  );
  return response.data;
}

export async function getAutomationTemplatesService(): Promise<AutomationTemplatesResponse> {
  const response = await instance.get<AutomationTemplatesResponse>(
    `${AUTOMATIONS_URL}/Templates`,
  );
  return response.data;
}

export async function updateAutomationTemplateService({
  id,
  name,
  description,
  eventId,
  payload,
}: Required<Pick<AutomationTemplatePayload, "id">> &
  Pick<
    AutomationTemplatePayload,
    "name" | "description" | "eventId" | "payload"
  >): Promise<AutomationTemplateResponse> {
  const response = await instance.put<AutomationTemplateResponse>(
    `${AUTOMATIONS_URL}/Templates/${id}`,
    { name, description, eventId, payload },
    jsonBodyConfig,
  );
  return response.data;
}

export async function getAutomationTemplateService(
  id: string,
): Promise<AutomationTemplateDetail> {
  const response = await instance.get<AutomationTemplateDetail>(
    `${AUTOMATIONS_URL}/Templates/${id}`,
  );
  return response.data;
}

export async function deleteAutomationTemplateService(
  id: string,
): Promise<AutomationTemplateResponse> {
  const response = await instance.delete<AutomationTemplateResponse>(
    `${AUTOMATIONS_URL}/Templates/${id}`,
  );
  return response.data;
}

export async function createAutomationTemplateFromFlowService(
  flowId: string,
): Promise<AutomationTemplateResponse> {
  const response = await instance.post<AutomationTemplateResponse>(
    `${AUTOMATIONS_URL}/Templates/from-flow/${flowId}`,
  );
  return response.data;
}

export async function addAutomationResourceService({
  automationId,
  body,
}: AddAutomationResourceArgs): Promise<AddAutomationResourceResponse> {
  const response = await instance.post<AddAutomationResourceResponse>(
    `${AUTOMATIONS_URL}/${automationId}/Resources`,
    body,
    jsonBodyConfig,
  );
  return response.data;
}

export async function addAutomationResourcesService(
  body: AddAutomationResourcesPayload,
): Promise<AddAutomationResourcesResponse> {
  const response = await instance.post<AddAutomationResourcesResponse>(
    `${AUTOMATIONS_URL}/Resources/Automations`,
    body,
    jsonBodyConfig,
  );
  return response.data;
}

export async function getAutomationResourceTypesService(): Promise<AutomationResourceTypesResponse> {
  const response = await instance.get<AutomationResourceTypesResponse>(
    `${AUTOMATIONS_URL}/ResourceTypes`,
  );
  return response.data;
}

export async function getAutomationResourcesService(
  params: GetAutomationResourcesParams,
): Promise<GetAutomationResourcesResponse> {
  const response = await instance.get<GetAutomationResourcesResponse>(
    `${AUTOMATIONS_URL}/Resources`,
    { params },
  );
  return response.data;
}

export async function removeAutomationResourceService(
  resourceBindingId: string,
): Promise<RemoveAutomationResourceResponse> {
  const response = await instance.delete<RemoveAutomationResourceResponse>(
    `${AUTOMATIONS_URL}/Resources/${resourceBindingId}`,
  );
  return response.data;
}

export async function createAutomationFlowFromTemplateService(
  id: string,
): Promise<AutomationTemplateResponse> {
  const response = await instance.post<AutomationTemplateResponse>(
    `${AUTOMATIONS_URL}/Templates/${id}/Flows`,
  );
  return response.data;
}
